import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Form, Input, Select, Tooltip, Row, Col, Typography, Divider, theme } from 'antd'
import { InfoCircleFilled, InfoCircleOutlined } from '@ant-design/icons'

import { selectLocaleSlice } from '../../store/locale-slice'
import { useAppSelector } from '../../store/hooks'
import { Banner } from '../../design-system/Banner'
import { DaysOfWeekField, DaysOfWeekFieldProps } from '../../design-system/DaysOfWeekField'
import { SegmentedToggle } from '../../design-system/SegmentedToggle'
import { TextField } from '../../design-system/TextField'
import { useDayNames } from '../../hooks/use-day-names'
import { numberOfDaysInMonth } from '../../util/number-of-days-in-month'
import { months } from '../../util/months'
import { filterOptions } from '../../util/filter-options'
import timezone from '@vacationtracker/shared/data/timezone'
import { isLocationNameTaken, placeLabel, countriesForPicker, subdivisionOptions, subdivisionTermKey, uniqueLocationName } from '@vacationtracker/shared/data/countries'
import { timezoneForPlace } from '@vacationtracker/shared/data/country-timezones'
import { filterCountryOptions } from '@vacationtracker/shared/functions/filter-countries'
import { publicHolidaysForPlace, statutoryPoliciesForPlace } from '../../util/location-facts'
import { useLeaveTypesApi } from '../../services/api/leave-type-templates-hook'

import IntlMessages from '../../util/IntlMessages'

import { IGetLocationById, IGetUserListShort } from '../../types/locations'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { getAverageWorkingHoursForLocation, HOURS_MAX, HOURS_MIN, HOURS_STEP } from '@vacationtracker/shared/functions/work-week'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'

const { Option } = Select
const { Title, Paragraph } = Typography

interface ILocationForm {
  location: IGetLocationById
  users: IGetUserListShort[]
  existingLocations?: { id: string; name?: string }[]
  workdayEnabled: boolean
  plan: SubscriptionPlan
  leaveTypes?: { id: string; name: string; isActive: boolean }[]
  form: {
    getFieldValue: Function
    setFieldsValue: Function
  }
  disableAll: boolean
}

interface IDays {
  value: number
  label: string
}

/**
 * DaysOfWeekField inside a Form.Item.
 *
 * Form.Item injects `value` and `onChange` by cloning its child, but it injects them as optional
 * and DaysOfWeekField requires both - and on create the store has no workWeek yet, which would
 * crash the component's `value.includes(...)`. This adapter absorbs both.
 */
const WorkWeekField = ({
  value,
  onChange,
  ...rest
}: { value?: number[]; onChange?: (value: number[]) => void } & Omit<DaysOfWeekFieldProps, 'value' | 'onChange'>): React.ReactElement => (
  <DaysOfWeekField {...rest} value={value ?? []} onChange={(next) => onChange?.(next)} />
)

const LocationForm = ({ location, users, existingLocations = [], form, workdayEnabled, disableAll, plan, leaveTypes = [] }: ILocationForm): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const { locale } = useAppSelector(selectLocaleSlice)
  const generateDaysForMonth = (numberOfDays: number) => {
    const days: IDays[] = []
    for (let i = 1; i <= numberOfDays; i++) {
      days.push({ value: i, label: `${i}` })
    }
    return days
  }

  // No form instance passed: the `form` prop here is a narrowed shape, and this component always
  // renders inside the <Form> that LocationFormPage owns, so the field context resolves it.
  const watchedFirstDayOfWeek = Form.useWatch<number>('firstDayOfWeek')
  const dayNames = useDayNames()
  const yesNoOptions = useMemo(
    () => [
      { value: true, label: formatMessage({ id: 'app.yes' }) },
      { value: false, label: formatMessage({ id: 'app.no' }) },
    ],
    [formatMessage]
  )

  const [selectedCountry, setSelectedCountry] = useState(location.countryCode ?? '')
  const [selectedSubdivision, setSelectedSubdivision] = useState(location.subdivisionCode ?? null)
  // Only a pick the admin makes counts: setFieldsValue does not fire the Select's onChange, so the
  // suggestion writing to the field cannot be mistaken for one.
  const [timezoneSetByHand, setTimezoneSetByHand] = useState(false)
  const subdivisions = selectedCountry ? subdivisionOptions(selectedCountry) : []

  // What the chosen place implies. The create page imports exactly this set, so the count offered
  // here and the holidays written cannot drift.
  const { data: leaveLaws } = useLeaveTypesApi()
  const statutoryPolicies = useMemo(
    () => statutoryPoliciesForPlace({ place: { countryCode: selectedCountry, subdivisionCode: selectedSubdivision }, leaveTypes, laws: leaveLaws, plan }),
    [selectedCountry, selectedSubdivision, leaveTypes, leaveLaws, plan]
  )

  const placeHolidays = useMemo(
    () => publicHolidaysForPlace({ countryCode: selectedCountry, subdivisionCode: selectedSubdivision }, locale.locale),
    [selectedCountry, selectedSubdivision, locale.locale]
  )

  // One sentence for what creating this location will do, rather than a fact per consequence. The
  // holiday library covers 203 of 250 countries and the leave laws fewer still, so each half of it
  // is conditional.
  const setupSummary = useMemo(() => {
    const holidays = placeHolidays?.holidays.length ?? 0
    const policies = statutoryPolicies.length
    if (holidays > 0 && policies > 0) {
      return formatMessage({ id: 'locationCreate.setup.summary' }, { holidays, policies })
    }
    if (policies > 0) {
      return formatMessage(
        { id: 'locationCreate.setup.summaryNoHolidays' },
        { policies, place: placeLabel({ countryCode: selectedCountry, subdivisionCode: selectedSubdivision }) }
      )
    }
    if (holidays > 0) {
      return formatMessage({ id: 'locationPlace.holidays.source' }, { place: placeHolidays?.place, count: holidays })
    }
    return undefined
  }, [placeHolidays, statutoryPolicies, selectedCountry, selectedSubdivision, formatMessage])

  const sortedCountries = countriesForPicker()

  const takenNames = existingLocations.map((existing) => existing.name ?? '').filter(Boolean)

  const suggestedName = (countryCode: string, subdivisionCode: string | null) => uniqueLocationName(placeLabel({ countryCode, subdivisionCode: subdivisionCode }), takenNames)

  // Keep the name in step with the chosen place - but only while it is still empty or still the
  // name we generated, so a name the admin typed is never overwritten.
  const autofillName = (countryCode: string, subdivisionCode: string | null) => {
    const current = String(form.getFieldValue('name') ?? '')
    // The place being replaced comes from state, not from the form: rc-field-form writes the new
    // value into its store before calling onChange, so the form already holds the incoming code.
    const previous = suggestedName(selectedCountry, selectedSubdivision)
    if (current !== '' && current !== previous) return

    const next = suggestedName(countryCode, subdivisionCode)
    if (next) {
      form.setFieldsValue({ name: next })
    }
  }

  // The form opens on the browser's timezone, which has nothing to do with the location being
  // created. Picking a place replaces it - until the admin chooses one themselves, which they are
  // entitled to do (a remote team kept on HQ time). A place we cannot map leaves the field alone,
  // so the browser's guess stays as the fallback rather than being cleared to nothing.
  const autofillTimezone = (countryCode: string, subdivisionCode: string | null) => {
    if (timezoneSetByHand) return
    const suggested = timezoneForPlace({ countryCode, subdivisionCode })
    if (suggested) {
      form.setFieldsValue({ timezone: suggested })
    }
  }

  // Clearing a Select hands back undefined, which JSON.stringify drops from the request body - and
  // the update handler only overwrites keys the event actually carries. Both have to become null.
  const onCountryChange = (iso?: string) => {
    autofillName(iso ?? '', null)
    autofillTimezone(iso ?? '', null)
    setSelectedCountry(iso ?? '')
    setSelectedSubdivision(null)
    form.setFieldsValue({ countryCode: iso ?? null, subdivisionCode: null })
  }

  // A country handed in on create gets the same name and time zone suggestions as one picked here.
  useEffect(() => {
    if (!location.id && location.countryCode) {
      autofillName(location.countryCode, null)
      autofillTimezone(location.countryCode, null)
    }
  }, [])

  const onSubdivisionChange = (code?: string) => {
    autofillName(selectedCountry, code ?? null)
    autofillTimezone(selectedCountry, code ?? null)
    setSelectedSubdivision(code ?? null)
    form.setFieldsValue({ subdivisionCode: code ?? null })
  }

  const [fiscalYearSelectDays, setFiscalYearSelectDays] = useState(generateDaysForMonth(numberOfDaysInMonth(location.yearStartMonth)))
  const [isFiscalYear, setResetQuotesStatus] = useState(() => {
    if (location.resetQuotas === 'USER_YEAR') {
      return 'USER_YEAR'
    } else {
      return 'FISCAL_YEAR'
    }
  })

  const onChangeMonthFiscalYear = (month: number) => {
    const numberOfDays = numberOfDaysInMonth(month)
    const days = generateDaysForMonth(numberOfDays)

    const yearStartDay = form.getFieldValue('yearStartDay')
    if (yearStartDay > numberOfDays) {
      form.setFieldsValue({ yearStartDay: 1 })
    }
    setFiscalYearSelectDays(days)
  }

  const onChangeResetQuotes = (status) => {
    setResetQuotesStatus(status)
  }

  const onChangeWorkday = (value) => {
    const roundedValue = Math.round(value * 2) / 2 // round to the nearest 0.5(if user puts 7.4 for example)
    form.setFieldsValue({ workHours: roundedValue })
  }

  return (
    <>
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
          <Title level={4}>
            <IntlMessages id="components.locationForm.settings.nameTitle" />
          </Title>
          <Paragraph type="secondary">
            <IntlMessages id="components.locationForm.settings.nameDescription" />
          </Paragraph>
          <Paragraph type="secondary">
            <IntlMessages id="components.locationForm.settings.nameDescription2" />
          </Paragraph>
        </Col>
        <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
          <Form.Item
            className="wrap-label"
            name="countryCode"
            label={<IntlMessages id="app.country" />}
            rules={[{ required: true, message: <IntlMessages id="locationPlace.country.required" /> }]}
          >
            <Select
              showSearch
              allowClear
              disabled={disableAll}
              filterOption={filterCountryOptions}
              onChange={onCountryChange}
              placeholder={formatMessage({ id: 'locationPlace.country.placeholder' })}
            >
              {sortedCountries.map((country) => (
                <Option key={country.iso} value={country.iso}>
                  {country.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {subdivisions.length > 0 && (
            <Form.Item className="wrap-label" name="subdivisionCode" label={<IntlMessages id={subdivisionTermKey(selectedCountry)} />}>
              <Select
                showSearch
                allowClear
                disabled={disableAll}
                filterOption={filterOptions}
                onChange={onSubdivisionChange}
                placeholder={formatMessage({ id: 'locationPlace.subdivision.placeholder' })}
              >
                {subdivisions.map((subdivision) => (
                  <Option key={subdivision.value} value={subdivision.value}>
                    {subdivision.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {/* Neutral icon, passed in rather than left to the tone: Banner colours its own icon with
              an inline style, and this theme maps colorInfo onto the brand purple. */}
          {setupSummary && (
            <Banner tone="info" icon={<InfoCircleFilled style={{ color: token.colorTextTertiary }} />} message={setupSummary} className="vt-location-form-summary" />
          )}
          <Form.Item
            name="name"
            className="wrap-label"
            label={<IntlMessages id="app.name" />}
            rules={[
              { required: true, message: <IntlMessages id="form.inputRequired" /> },
              {
                // Catch a duplicate here rather than letting the save round-trip and fail.
                validator: (_rule, value: string) =>
                  isLocationNameTaken(value, takenNames) ? Promise.reject(formatMessage({ id: 'error.CannotDuplicateName' })) : Promise.resolve(),
              },
            ]}
          >
            <Input maxLength={100} placeholder={`${formatMessage({ id: 'components.locationForm.settings.namePlaceholder' })}`} />
          </Form.Item>
          <Form.Item name="users" className="wrap-label" label={<IntlMessages id="app.users" />}>
            <Select mode="multiple" disabled={disableAll} filterOption={filterOptions}>
              {users.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className="wrap-label"
            name="timezone"
            label={<IntlMessages id="app.timezone" />}
            rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
          >
            <Select showSearch filterOption={filterOptions} onChange={() => setTimezoneSetByHand(true)}>
              {timezone.map((tz) => (
                <Option key={tz.id} value={tz.id}>
                  {tz.text}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
          <Title level={4}>
            <IntlMessages id="components.locationForm.settings.makeDefaultTitle" />
          </Title>
          <Paragraph type="secondary">
            <IntlMessages id="components.locationForm.settings.makeDefaultDescription" />
          </Paragraph>
        </Col>
        <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
          <Form.Item name="isDefault">
            <SegmentedToggle required label={<IntlMessages id="components.locationForm.defaultLocation" />} options={yesNoOptions} disabled={disableAll} />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
          <Title level={4}>
            <IntlMessages id="components.locationForm.settings.workWeekTitle" />
          </Title>
          <Paragraph type="secondary">
            <IntlMessages id="components.locationForm.settings.workWeekDescription" />
          </Paragraph>
        </Col>
        <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
          <Form.Item
            name="workWeek"
            className="wrap-label"
            label={<IntlMessages id="app.workweek" />}
            rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
          >
            {/* firstDayOfWeek is a sibling field on this form, so the chip order follows it live. */}
            <WorkWeekField firstDayOfWeek={watchedFirstDayOfWeek ?? 1} dayNames={dayNames} ariaLabel={formatMessage({ id: 'app.workweek' })} />
          </Form.Item>
          <Form.Item className="wrap-label" name="firstDayOfWeek" required={true} label={<IntlMessages id="notifications.weekStartsOn" />}>
            <Select style={{ width: 130 }}>
              <Select.Option key="0" value={0}>
                <IntlMessages id="app.sunday" />
              </Select.Option>
              )
              <Select.Option key="1" value={1}>
                <IntlMessages id="app.monday" />
              </Select.Option>
              )
            </Select>
          </Form.Item>
          <Form.Item
            name="workHours"
            className="wrap-label"
            label={
              <>
                <IntlMessages id="app.workday" />
                <Tooltip className="info-tooltip" title={<IntlMessages id="app.feature.access" />}>
                  <InfoCircleOutlined />
                </Tooltip>
              </>
            }
          >
            <TextField
              ariaLabel="Workday hours"
              variant="number"
              step={HOURS_STEP}
              min={HOURS_MIN}
              max={HOURS_MAX}
              style={{ width: 70 }}
              // TODO workday - when we add UI for custom work hours per day per location, change this:
              defaultValue={getAverageWorkingHoursForLocation(location.workHours)}
              onChange={onChangeWorkday}
              disabled={!workdayEnabled}
            />
            &nbsp;
            <IntlMessages id="app.hours" />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
          <Title level={4}>
            <IntlMessages id="components.locationForm.settings.quotasTitle" />
          </Title>
          <Paragraph type="secondary">
            <IntlMessages id="components.locationForm.settings.quotasDescription" />
          </Paragraph>
          <Paragraph type="secondary">
            <IntlMessages id="components.locationForm.settings.quotasDescription2" />
          </Paragraph>
        </Col>
        <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
          {isFiscalYear === 'FISCAL_YEAR' && (
            <>
              <Form.Item className="wrap-label" label={<IntlMessages id="location.fiscalYearStart" />} required={true} style={{ marginBottom: 0 }}>
                <Form.Item name="yearStartMonth" style={{ display: 'inline-block', width: 140 }}>
                  <Select<number> style={{ width: 130, marginRight: 10 }} onChange={(month) => onChangeMonthFiscalYear(month)}>
                    {months.map((month) => (
                      <Select.Option key={month.value} value={month.value}>
                        <IntlMessages id={month.label} />
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="yearStartDay" className="vr-md-ml-10" style={{ display: 'inline-block', width: 80 }}>
                  <Select<number> style={{ width: 80 }}>
                    {fiscalYearSelectDays.map((day) => (
                      <Select.Option key={day.value} value={day.value}>
                        {day.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form.Item>
            </>
          )}
          <Form.Item name="resetQuotas" className="wrap-label" label={<IntlMessages id="locations.resetQuotas" />} required={true}>
            <Select onChange={onChangeResetQuotes} style={{ width: locale.locale === LocaleEnum.en ? 200 : 300 }}>
              <Select.Option key="fiscalYear" value="FISCAL_YEAR">
                <IntlMessages id="components.locationForm.fiscalYear" />
              </Select.Option>
              )
              <Select.Option key="userYear" value="USER_YEAR">
                <IntlMessages id="users.employmentDate" />
              </Select.Option>
              )
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}

export default LocationForm
